import {
  type AttributeGroup,
  type PickerTarget,
  type ProductGroupTreeForm,
  type ProductLeaf,
  type SectionPath,
  type SkuPickerSelection,
  isAttributeGroup,
  isProductLeaf,
  newSectionForDepth,
} from './tree';

export const MAX_ATTRIBUTES = 3;

function withTopLevelGroups(
  form: ProductGroupTreeForm,
  transform: (groups: AttributeGroup[]) => AttributeGroup[],
): ProductGroupTreeForm {
  return { ...form, groups: transform(form.groups) };
}

function withAttributeGroupAtPath(
  form: ProductGroupTreeForm,
  path: SectionPath,
  transform: (group: AttributeGroup) => AttributeGroup,
): ProductGroupTreeForm {
  if (path.length === 0) return form;
  const updateGroup = (group: AttributeGroup, depth: number): AttributeGroup => {
    if (depth === path.length - 1) return transform(group);
    const childIndex = path[depth + 1]!;
    return {
      ...group,
      children: group.children.map((child, index) => {
        if (index !== childIndex || !isAttributeGroup(child)) return child;
        return updateGroup(child, depth + 1);
      }),
    };
  };
  return {
    ...form,
    groups: form.groups.map((group, index) => (index !== path[0] ? group : updateGroup(group, 0))),
  };
}

export function updateAttributeNameInForm(
  form: ProductGroupTreeForm,
  attributeIndex: number,
  value: string,
): ProductGroupTreeForm {
  const attributes = [...form.attributes];
  attributes[attributeIndex] = value;
  return { ...form, attributes };
}

export function addAttributeToForm(form: ProductGroupTreeForm): ProductGroupTreeForm {
  if (form.attributes.length >= MAX_ATTRIBUTES) return form;
  const wrapWithEmptyAttribute = (groups: AttributeGroup[]): AttributeGroup[] =>
    groups.map((group) => {
      const leafSiblings = group.children.filter(isProductLeaf);
      const subGroupSiblings = group.children.filter(isAttributeGroup);
      const newChildren: (AttributeGroup | ProductLeaf)[] = [];
      if (leafSiblings.length > 0) {
        newChildren.push({ value: '', children: leafSiblings });
      }
      subGroupSiblings.forEach((subGroup) => {
        newChildren.push({ ...subGroup, children: wrapWithEmptyAttribute([subGroup])[0]!.children });
      });
      if (newChildren.length === 0) {
        newChildren.push({ value: '', children: [] });
      }
      return { ...group, children: newChildren };
    });
  return { ...form, attributes: [...form.attributes, ''], groups: wrapWithEmptyAttribute(form.groups) };
}

export function removeLastAttributeFromForm(form: ProductGroupTreeForm): ProductGroupTreeForm {
  if (form.attributes.length <= 1) return form;
  const flattenDeepestLevel = (groups: AttributeGroup[], depth: number): AttributeGroup[] =>
    groups.map((group) => {
      const isAtDeepest = depth === form.attributes.length - 2;
      const newChildren: (AttributeGroup | ProductLeaf)[] = [];
      group.children.forEach((child) => {
        if (isProductLeaf(child)) {
          newChildren.push(child);
        } else if (isAtDeepest) {
          child.children.forEach((grandchild) => {
            if (isProductLeaf(grandchild)) newChildren.push(grandchild);
          });
        } else {
          newChildren.push({ ...child, children: flattenDeepestLevel([child], depth + 1)[0]!.children });
        }
      });
      return { ...group, children: newChildren };
    });
  return { ...form, attributes: form.attributes.slice(0, -1), groups: flattenDeepestLevel(form.groups, 0) };
}

export function updateSectionValueInForm(
  form: ProductGroupTreeForm,
  path: SectionPath,
  value: string,
): ProductGroupTreeForm {
  return withAttributeGroupAtPath(form, path, (group) => ({ ...group, value }));
}

export function moveSectionWithinParentInForm(
  form: ProductGroupTreeForm,
  path: SectionPath,
  direction: 1 | -1,
): ProductGroupTreeForm {
  const indexInParent = path[path.length - 1]!;
  const targetIndex = indexInParent + direction;
  const swapInArray = <T>(arr: T[]): T[] => {
    if (targetIndex < 0 || targetIndex >= arr.length) return arr;
    const next = [...arr];
    const moved = next[indexInParent]!;
    const displaced = next[targetIndex]!;
    next[indexInParent] = displaced;
    next[targetIndex] = moved;
    return next;
  };
  if (path.length === 1) {
    return withTopLevelGroups(form, swapInArray);
  }
  const parentPath = path.slice(0, -1);
  return withAttributeGroupAtPath(form, parentPath, (parent) => ({
    ...parent,
    children: swapInArray(parent.children),
  }));
}

export function removeSectionFromForm(form: ProductGroupTreeForm, path: SectionPath): ProductGroupTreeForm {
  if (path.length === 1) {
    const [topIndex] = path;
    return withTopLevelGroups(form, (groups) => groups.filter((_, index) => index !== topIndex));
  }
  const parentPath = path.slice(0, -1);
  const indexInParent = path[path.length - 1]!;
  return withAttributeGroupAtPath(form, parentPath, (parent) => ({
    ...parent,
    children: parent.children.filter((_, index) => index !== indexInParent),
  }));
}

export function applyPickerSelectionToForm(
  form: ProductGroupTreeForm,
  target: PickerTarget,
  selection: SkuPickerSelection,
): ProductGroupTreeForm {
  if (target.kind === 'replace') {
    const { path: parentPath, childIndex } = target;
    return withAttributeGroupAtPath(form, parentPath, (parent) => ({
      ...parent,
      children: parent.children.map((child, index) => {
        if (index !== childIndex || !isProductLeaf(child)) return child;
        const updated: ProductLeaf = { sku: selection.sku };
        if (selection.productName !== undefined) updated.productName = selection.productName;
        return updated;
      }),
    }));
  }
  const newLeaf: ProductLeaf = { sku: selection.sku };
  if (selection.productName !== undefined) newLeaf.productName = selection.productName;
  return withAttributeGroupAtPath(form, target.path, (group) => ({
    ...group,
    children: [...group.children, newLeaf],
  }));
}

export function addSubSectionToSectionInForm(
  form: ProductGroupTreeForm,
  path: SectionPath,
): ProductGroupTreeForm {
  const subSectionDepth = path.length;
  const newSubSection = newSectionForDepth(subSectionDepth, form.attributes.length);
  return withAttributeGroupAtPath(form, path, (group) => ({
    ...group,
    children: [...group.children, newSubSection],
  }));
}

export function removeLeafFromForm(
  form: ProductGroupTreeForm,
  parentPath: SectionPath,
  childIndex: number,
): ProductGroupTreeForm {
  return withAttributeGroupAtPath(form, parentPath, (parent) => ({
    ...parent,
    children: parent.children.filter((_, index) => index !== childIndex),
  }));
}

export function appendNewTopLevelSectionToForm(form: ProductGroupTreeForm): ProductGroupTreeForm {
  return withTopLevelGroups(form, (groups) => [...groups, newSectionForDepth(0, form.attributes.length)]);
}
